import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faCheck, faFileImport, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useCriarInventario, useImportarEstoqueControlado } from '../../../../hooks/useSngpc';
import { useToast } from '../../../../providers/ToastProvider';

interface ModalCriarInventarioProps {
    show: boolean;
    onHide: () => void;
    modalAction: 'importar' | 'manual';
}

const ModalCriarInventario: React.FC<ModalCriarInventarioProps> = ({ show, onHide, modalAction }) => {
    // Garantir que sempre use a data atual no fuso horário local do Brasil
    const getDataBrasil = () => {
        const agora = new Date();
        // Usar Intl.DateTimeFormat para obter a data no fuso horário brasileiro
        const formatter = new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Sao_Paulo' });
        return formatter.format(agora);
    };

    const [dataInventario, setDataInventario] = useState(getDataBrasil());
    const { showToast } = useToast();

    const criarInventarioMutation = useCriarInventario();
    const importarEstoqueMutation = useImportarEstoqueControlado();

    const handleConfirmar = async () => {
        // Validação manual no frontend primeiro
        const hoje = new Date();
        const dataEscolhida = new Date(dataInventario);

        if (dataEscolhida > hoje) {
            showToast('A data do inventário não pode ser futura.', 'error');
            return;
        }

        const seteRiasAtras = new Date();
        seteRiasAtras.setDate(hoje.getDate() - 7);

        if (dataEscolhida < seteRiasAtras) {
            showToast('A data do inventário não pode ter mais de 7 dias de atraso.', 'error');
            return;
        }

        if (modalAction === 'importar') {
            // Primeiro cria o inventário, depois importa
            try {
                const resultado = await criarInventarioMutation.mutateAsync({ data: dataInventario });
                if (resultado.isSuccess) {
                    await importarEstoqueMutation.mutateAsync();
                    onHide();
                }
            } catch (error) {
                console.error('Erro ao criar inventário:', error);
            }
        } else {
            // Cria inventário vazio
            try {
                const resultado = await criarInventarioMutation.mutateAsync({ data: dataInventario });
                if (resultado.isSuccess) {
                    onHide();
                }
            } catch (error) {
                console.error('Erro ao criar inventário:', error);
            }
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon
                        icon={modalAction === 'importar' ? faFileImport : faPlus}
                        className="me-2 text-primary-emphasis"
                    />
                    {modalAction === 'importar' ? 'Importar Estoque Controlado' : 'Criar Inventário Manual'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            Data do Inventário
                        </Form.Label>
                        <Form.Control
                            type="date"
                            value={dataInventario}
                            onChange={(e) => setDataInventario(e.target.value)}
                            max={getDataBrasil()}
                        />
                        <Form.Text className="text-body-secondary">
                            A data não pode ser futura nem ter mais de 7 dias de atraso.
                        </Form.Text>
                    </Form.Group>

                    {modalAction === 'importar' && (
                        <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle">
                            <div className="text-info-emphasis">
                                <strong>Importação automática:</strong> Todos os produtos controlados com estoque serão
                                automaticamente incluídos no inventário.
                            </div>
                        </Alert>
                    )}

                    {modalAction === 'manual' && (
                        <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle">
                            <div className="text-info-emphasis">
                                <strong>Inventário manual:</strong> O inventário será criado vazio.
                                Você poderá adicionar produtos manualmente.
                            </div>
                        </Alert>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleConfirmar}
                    disabled={criarInventarioMutation.isPending || importarEstoqueMutation.isPending}
                >
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    {criarInventarioMutation.isPending || importarEstoqueMutation.isPending
                        ? 'Processando...'
                        : 'Confirmar'
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalCriarInventario; 